import type { ButtonHTMLAttributes, FC, ReactNode } from 'react'
import type { LinkProps } from 'next/link'

import Link from 'next/link'
import { LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'gold' | 'success' | 'info' | 'outline' | 'ghost' | 'danger' | 'subtle'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl'

const VARIANTS: Record<ButtonVariant, string> = {
    gold: 'bg-gold text-primary-foreground hover:bg-gold-bright shadow-[0_10px_30px_-14px_rgba(212,175,90,0.7)]',
    success: 'bg-success text-success-foreground hover:bg-success-strong shadow-[0_10px_30px_-14px_rgba(52,209,122,0.65)]',
    info: 'bg-info text-info-foreground hover:bg-info-strong shadow-[0_10px_30px_-14px_rgba(37,99,235,0.7)]',
    outline: 'border border-gold/35 text-foreground hover:border-gold/70 hover:bg-white/5',
    ghost: 'text-foreground hover:bg-white/6',
    danger: 'bg-[#dc2626] text-white hover:bg-[#b91c1c]',
    subtle: 'bg-secondary text-secondary-foreground hover:bg-accent border border-border'
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-9 px-3.5 text-sm gap-1.5 rounded-lg',
    md: 'h-11 px-5 text-[15px] gap-2 rounded-xl',
    lg: 'h-13 px-6 text-base gap-2.5 rounded-xl',
    xl: 'h-15 px-8 text-lg gap-3 rounded-2xl'
}

export const buttonClass = (variant: ButtonVariant = 'gold', size: ButtonSize = 'md', className?: string): string =>
    cn(
        'inline-flex select-none items-center justify-center font-semibold whitespace-nowrap transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-55 active:scale-[0.99]',
        VARIANTS[variant],
        SIZES[size],
        className
    )

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
    loading?: boolean
    children?: ReactNode
}

export const Button: FC<ButtonProps> = ({ variant = 'gold', size = 'md', loading = false, className, children, disabled, type = 'button', ...rest }): ReactNode => {
    return (
        <button
            type={type}
            className={buttonClass(variant, size, className)}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            {...rest}
        >
            {loading && <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />}
            {children}
        </button>
    )
}

interface ButtonLinkProps extends LinkProps {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
    children?: ReactNode
    target?: string
    rel?: string
}

export const ButtonLink: FC<ButtonLinkProps> = ({ variant = 'gold', size = 'md', className, children, ...rest }): ReactNode => {
    return (
        <Link className={buttonClass(variant, size, className)} {...rest}>
            {children}
        </Link>
    )
}
